import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/cn";
import { FOCUS_RING } from "../../lib/focusRing";
import { Spinner } from "./Spinner";

// Module-internal cva config (react-refresh: components-only exports).
// Primary and secondary mirror the landing hero's two CTAs (Hero.tsx); keep
// them in step. One primary per view (docs/v2-core/STYLE.md).
const buttonVariants = cva(
  cn(
    "inline-flex items-center justify-center gap-2 rounded-full font-medium transition disabled:pointer-events-none disabled:opacity-50",
    FOCUS_RING,
  ),
  {
    variants: {
      variant: {
        primary:
          "bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-soft hover:shadow-glow",
        secondary: "border border-sky-200 bg-white text-sky-700 shadow-soft hover:bg-sky-50",
        // Quiet action that still reads on a pastel wash (ghost text alone can get lost there).
        soft: "bg-white/75 text-neutral-800 shadow-soft ring-1 ring-white hover:bg-white hover:text-sky-700",
        ghost: "text-neutral-700 hover:bg-white/70 hover:text-neutral-900",
        destructive: "bg-red-600 text-white shadow-soft hover:bg-red-700",
        destructiveOutline: "border border-red-200 bg-white text-red-600 hover:bg-red-50",
      },
      size: {
        sm: "px-3.5 py-1.5 text-sm",
        md: "px-5 py-2 text-sm",
        lg: "px-6 py-3 text-base",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render onto the child element instead of a <button> (e.g. a wouter <Link>). */
  asChild?: boolean;
  /** Spinner + disabled + aria-busy. Real <button> renders only (not asChild). */
  isLoading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    asChild = false,
    isLoading = false,
    type,
    disabled,
    children,
    ...props
  },
  ref,
) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      {...(asChild
        ? {}
        : {
            type: type ?? "button",
            disabled: disabled || isLoading,
            "aria-busy": isLoading || undefined,
          })}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {isLoading && <Spinner size="sm" className="text-current" />}
          {children}
        </>
      )}
    </Comp>
  );
});
